// 잘린(ellipsis) 셀에서만 MUI Tooltip을 띄우는 공용 컴포넌트.
// DataGridPanel이 renderCell 미지정 컬럼에 자동 주입하며, 커스텀 renderCell(span/Link)도
// 이 컴포넌트로 직접 감싸 사용한다(레시피 문서 참고). 짧은 셀은 어떤 툴팁(네이티브 title 포함)도 뜨지 않는다.

import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from "@mui/x-data-grid";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";

export interface OverflowTooltipProps {
  // 셀에 표시하고, 잘렸을 때 툴팁으로 보여줄 전체 텍스트
  value?: string | number | null;
  // 텍스트 대신 렌더할 노드(예: <Link>). 미지정 시 value를 그대로 렌더한다.
  children?: React.ReactNode;
}

export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({ value, children }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // 빈 값은 공백으로 표시("없음" 미적용 — 기존 그리드의 빈 칸 표시 관례를 유지)
  const text = value == null || value === "" ? "" : String(value);

  const checkOverflow = () => {
    const el = boxRef.current;
    if (!el) return;
    const overflowing = el.scrollWidth > el.clientWidth;
    // 값이 바뀔 때만 set — 매 mouseenter마다 불필요한 리렌더를 막는다.
    if (overflowing !== isOverflowing) {
      setIsOverflowing(overflowing);
    }
  };

  return (
    <Tooltip
      // Defect A: disableHoverListener={!isOverflowing}를 쓰지 않는다.
      // MUI Tooltip.js:483-484는 disableHoverListener가 true면 onMouseOver를 아예 부착하지 않는다.
      // 초기 isOverflowing=false 상태에서 첫 호버 시 리스너가 없으면 측정 후 true가 되어도
      // "첫 호버에 열리지 않는" 결함이 생긴다. 그래서 hover 리스너는 항상 부착하고,
      // 대신 title을 비워서(Tooltip.js:435 — 빈 title은 열리지 않음) "잘렸을 때만" 조건을 건다.
      title={isOverflowing ? text : ""}
      arrow
      enterDelay={300}
      // Hysteresis 방지: Tooltip은 모듈 전역 hystersisOpen 플래그를 쓰는데, 빈 title 셀도
      // handleOpen에서 이 플래그를 true로 만든다(Tooltip.js:297-299). 이후 800ms 안에 다른
      // 셀로 진입하면 enterDelay 대신 enterNextDelay(기본 0)가 적용되어 300ms 지연 없이
      // 즉시 열려버린다. enterDelay와 동일하게 300으로 맞춰 항상 300ms를 보장한다.
      enterNextDelay={300}
      // Defect B: 기본(interactive) Tooltip은 popper가 포인터 이벤트를 받는다. popper는
      // 포털로 렌더되지만 React 합성 이벤트는 React 트리를 따라 버블링되어 GridRow의
      // onClick(GridRow.js:152-185)까지 도달할 수 있다 — 그 결과 툴팁 위를 클릭했는데
      // 앵커 행(툴팁을 띄운 행)의 상세로 이동해버린다. disableInteractive로 popper를
      // pointer-events:none으로 만들어 클릭이 실제 아래 요소(다른 행)로 전달되게 한다.
      // popper에 onClick stopPropagation을 다는 방식은 쓰지 않는다 — 아래 행 클릭까지
      // 삼켜버려 사용자 의도를 잃는다.
      disableInteractive
      slotProps={{ tooltip: { sx: { fontSize: "14px" } } }}
      // describeChild 결정: describeChild를 켜면 닫힌 상태에서도 자식에 네이티브 title을
      // 기록해(Tooltip.js:455) 이중 툴팁이 재발한다. 그래서 describeChild는 쓰지 않고
      // 기본 동작(자식에 aria-label 부여)을 받아들인다 — 잘리지 않은 셀은 aria-label=""이
      // 되지만, 셀 텍스트 자체가 이미 스크린리더로 읽히는 요소라 실사용 영향은 작다.
    >
      <Box
        ref={boxRef}
        onMouseEnter={checkOverflow}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          width: "100%",
          ".MuiDataGrid-cell--textCenter &": { textAlign: "center" },
          ".MuiDataGrid-cell--textRight &": { textAlign: "right" },
        }}
      >
        {children ?? text}
      </Box>
    </Tooltip>
  );
};

// renderCell 없는 텍스트 컬럼용 기본 렌더러. formattedValue를 사용해 valueGetter/valueFormatter
// 결과를 반영한다. 객체 등 비-원시 값은 String()으로 폴백, null/undefined는 빈 칸으로 표시한다.
export const renderOverflowCell = (params: GridRenderCellParams): React.ReactNode => {
  const raw = params.formattedValue;
  const value =
    raw == null || typeof raw === "string" || typeof raw === "number" ? raw : String(raw);
  return <OverflowTooltip value={value} />;
};

// renderCell 미지정 컬럼에만 renderOverflowCell을 주입한 새 배열을 반환한다.
// 제외: renderCell을 이미 지정한 컬럼, type "actions"/"boolean", 체크박스 선택 컬럼.
// 입력 배열/객체는 변이하지 않는다.
export function withOverflowTooltip<R extends GridValidRowModel>(
  columns: readonly GridColDef<R>[]
): GridColDef<R>[] {
  return columns.map((col) => {
    if (
      col.renderCell ||
      col.type === "actions" ||
      col.type === "boolean" ||
      col.field === GRID_CHECKBOX_SELECTION_FIELD
    ) {
      return col;
    }
    return { ...col, renderCell: renderOverflowCell };
  });
}
